import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SettingDef, SettingsView } from '@project/contracts'
import { Button, Notice, Panel, Select } from '../design/index.js'
import { useApi } from '../app/api.js'
import { ErrorBlock, Loading, messageFor } from '../framework/feedback.js'
import { formatDate } from '../framework/format.js'
import { SettingLookup } from './admin/SettingLookup.js'
import { Link } from '../app/Link.js'
import { paths } from '../app/router.js'

/**
 * SET1-022 — `Enable External Integration`, read-only, with `Integration To`
 * under it. The connector is chosen on the integration page, so this shows
 * the answer and links there rather than holding a second copy of it.
 */
const ExternalIntegration = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const config = useQuery({ queryKey: ['integrationConfig', api.companyId], queryFn: () => api.integrationConfig(), retry: false })
  const label = config.data?.connectorLabel ?? null
  const on = Boolean(label)
  return (
    <div className="setting">
      <div className="setting__body">
        <div className="setting__label">Enable External Integration</div>
        <p className="setting__blurb">You can integrate this system with other applications.</p>
        <div className="setting__child">
          <div className="setting__label">Integration To</div>
          <div style={{ width: 260, marginTop: 6 }}>
            <Select value={label ?? ''} disabled aria-label="Integration To"
              options={label ? [{ value: label, label }] : []} placeholder="Not connected" onChange={() => {}} />
          </div>
          <p className="setting__meta">
            <Link to={paths.admin(companyId, 'integration')}>Set up on ERP/Accounting Integration</Link>
          </p>
        </div>
      </div>
      <div className="setting__control">
        <button type="button" role="switch" aria-checked={on} aria-label="Enable External Integration" disabled
          className={on ? 'switch switch--on' : 'switch'}>
          <span className="switch__knob" />
          <span className="switch__text">{on ? 'Yes' : 'No'}</span>
        </button>
      </div>
    </div>
  )
}

/**
 * Business settings — docs/11 §Setup → Settings.
 *
 * A long list of switches, each with the paragraph that explains it, because
 * the person throwing one of these is deciding whether a rule refuses or
 * merely warns. Every switch says what it will do and, once changed, who
 * changed it and when — the observed system shows "Inventory enabled on
 * 17-Dec-2024" and that stamp is worth keeping.
 */
export const SettingsPage = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const client = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  const defs = useQuery({ queryKey: ['settingDefs'], queryFn: () => api.settingDefs(), staleTime: 10 * 60_000 })
  const view = useQuery({ queryKey: ['settings', api.companyId], queryFn: () => api.settings() })

  const save = useMutation({
    mutationFn: ({ key, value }: { key: string; value: boolean | number | string }) => api.setSetting(key, value),
    onSuccess: (next: SettingsView, variables) => {
      client.setQueryData(['settings', api.companyId], next)
      // A setting can put a document type on or off the menu, so the menu and
      // anything already fetched under the old answer are no longer trusted.
      void client.invalidateQueries({ queryKey: ['menu'] })
      void client.invalidateQueries({ queryKey: ['docDefs'] })
      setError(null)
      setSaved(variables.key)
    },
    onError: (err) => {
      setError(messageFor(err))
      setSaved(null)
    },
  })

  const groups = useMemo(() => {
    const byGroup = new Map<string, SettingDef[]>()
    for (const def of defs.data ?? []) {
      const list = byGroup.get(def.group)
      if (list) list.push(def)
      else byGroup.set(def.group, [def])
    }
    return [...byGroup.entries()]
  }, [defs.data])

  if (defs.isLoading || view.isLoading) return <div className="page"><Loading /></div>
  if (defs.error) return <div className="page"><ErrorBlock error={defs.error} /></div>
  if (view.error) return <div className="page"><ErrorBlock error={view.error} /></div>

  const values = view.data?.values ?? {}
  const changed = view.data?.changed ?? {}

  /** The control a setting is answered with: a switch, a pick, a lookup or a box. */
  const controlFor = (def: SettingDef): JSX.Element => {
    const stamp = changed[def.key]
    const busy = save.isPending && save.variables?.key === def.key
    return (
      <div className="setting__control">
        {def.kind === 'boolean' ? (
          <button
            type="button"
            role="switch"
            aria-checked={values[def.key] === true}
            aria-label={def.label}
            className={values[def.key] === true ? 'switch switch--on' : 'switch'}
            // One way: once switched on and recorded, it stays on (SET-005).
            disabled={busy || (def.oneWay === true && values[def.key] === true && Boolean(stamp))}
            onClick={() => save.mutate({ key: def.key, value: values[def.key] !== true })}
          >
            <span className="switch__knob" />
            <span className="switch__text">{values[def.key] === true ? 'On' : 'Off'}</span>
          </button>
        ) : def.kind === 'select' ? (
          <div style={{ width: 200 }}>
            <Select
              value={String(values[def.key] ?? def.defaultValue)}
              onChange={(value) => save.mutate({ key: def.key, value })}
              options={[...(def.options ?? [])]}
              aria-label={def.label}
            />
          </div>
        ) : def.kind === 'lookup' ? (
          <SettingLookup
            def={def}
            value={String(values[def.key] ?? '')}
            busy={busy}
            onSave={(value) => save.mutate({ key: def.key, value })}
          />
        ) : (
          <TextSetting
            def={def}
            value={String(values[def.key] ?? '')}
            busy={busy}
            onSave={(value) => save.mutate({ key: def.key, value })}
          />
        )}
      </div>
    )
  }

  /**
   * One setting, and the options nested under it while it is on — the
   * reference's Start using inventory with its two checkboxes, the IP
   * restriction with its networks (SET1-018, SET1-021).
   */
  const row = (def: SettingDef): JSX.Element => {
    const stamp = changed[def.key]
    const children = (defs.data ?? []).filter((c) => c.parent === def.key)
    // SET1-020 — once the precision is 3 there is nothing left to choose.
    const precision = def.key === 'quantity_decimals'
    const settled = precision && (String(values[def.key] ?? def.defaultValue) === '3' || Boolean(stamp))
    return (
      <div className="setting">
        <div className="setting__body">
          <div className="setting__label">
            {precision ? `Current decimal precision for quantities is ${String(values[def.key] ?? def.defaultValue)}` : def.label}
          </div>
          <p className="setting__blurb">{def.blurb}</p>
          <p className="setting__meta">
            {def.effect}
            {stamp ? ` · Set ${formatDate(stamp.at.slice(0, 10))}${stamp.by ? ` by ${stamp.by}` : ''}` : ''}
            {def.irreversible && stamp ? ' · This cannot be changed again.' : ''}
            {def.oneWay && stamp && values[def.key] === true ? ' · Switched on; it stays on.' : ''}
          </p>
          {saved === def.key && !save.isPending ? <p className="setting__saved">Saved.</p> : null}
          {values[def.key] === true ? children.map((c) => (
            <div className="setting__child" key={c.key}>
              {c.kind === 'boolean' ? (
                <label className="setting__check">
                  <input type="checkbox" checked={values[c.key] === true}
                    disabled={save.isPending && save.variables?.key === c.key}
                    onChange={(e) => save.mutate({ key: c.key, value: e.currentTarget.checked })} />
                  <span className="setting__label">{c.label}</span>
                </label>
              ) : (
                <>
                  <div className="setting__label">{c.label}</div>
                  <div style={{ marginTop: 6 }}>{controlFor(c)}</div>
                </>
              )}
              <p className="setting__blurb">{c.blurb}</p>
              {saved === c.key && !save.isPending ? <p className="setting__saved">Saved.</p> : null}
            </div>
          )) : null}
        </div>
        {settled ? null : controlFor(def)}
      </div>
    )
  }

  return (
    <div className="page">
      <h1 className="page-title">Settings</h1>
      <p className="page-subtitle">
        How this company works. Each of these changes what the system does, not how it looks.
      </p>

      {error ? <div style={{ margin: '18px 0' }}><Notice kind="bad">{error}</Notice></div> : null}

      {groups.map(([group, items]) => (
        <Panel key={group} title={group}>
          <div className="settings">
            {items.filter((d) => !d.parent).map((d) => (
              <div key={d.key}>
                {row(d)}
                {d.key === 'ewaybill_enabled' ? <ExternalIntegration companyId={companyId} /> : null}
              </div>
            ))}
          </div>
        </Panel>
      ))}
    </div>
  )
}

/** Text and number settings save on a press, not on every keystroke. */
const TextSetting = ({
  def, value, busy, onSave,
}: {
  def: SettingDef
  value: string
  busy: boolean
  onSave: (value: string) => void
}) => {
  const [draft, setDraft] = useState(value)
  const dirty = draft.trim() !== value.trim()

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
      <input
        className="control"
        style={{ width: 260 }}
        value={draft}
        aria-label={def.label}
        inputMode={def.kind === 'number' ? 'numeric' : undefined}
        onChange={(e) => setDraft(e.currentTarget.value)}
      />
      <Button kind="ghost" disabled={busy || !dirty} onClick={() => onSave(draft.trim())}>
        Save
      </Button>
    </div>
  )
}
